import { useState, useEffect, useRef } from 'react'
import { EMOJI_CATEGORIES, searchEmojis } from '../data/emojis.js'

/**
 * EmojiPickerButton — small 😀 button that opens a floating picker.
 *
 * Usage:
 *   <EmojiPickerButton
 *     textareaRef={ref}                   // optional — inserts at caret
 *     onInsert={(emoji) => setText(...)}  // called with the emoji
 *     value={text}                        // current textarea text
 *     setValue={setText}                  // setter for controlled text
 *     direction="above"                   // 'above' | 'below'
 *   />
 *
 * If `textareaRef` + `value` + `setValue` are all supplied, we insert at
 * the current selection and restore the caret just after. Otherwise we
 * call onInsert and let the parent handle the text.
 */
const POP_WIDTH = 280
const POP_MAX_HEIGHT = 320

export default function EmojiPickerButton({
  textareaRef,
  value,
  setValue,
  onInsert,
  direction = 'above',
  size = 28,
  title = 'Add emoji',
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeCat, setActiveCat] = useState(EMOJI_CATEGORIES[0].id)
  const [popPos, setPopPos] = useState({ left: 0, top: 0, flipped: false })
  const btnRef = useRef(null)
  const popRef = useRef(null)
  const searchRef = useRef(null)

  // Position the popover relative to the viewport whenever it opens or the
  // window resizes. Using `position: fixed` means the popover escapes any
  // overflow-hidden ancestor (the DM panel has overflow:hidden for its
  // rounded corners) and is clamped within the viewport.
  const reposition = () => {
    const btn = btnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Prefer anchoring the popover's LEFT edge to the button's left edge,
    // but clamp so the popover doesn't overflow the viewport on either side.
    let left = Math.min(
      Math.max(margin, rect.left),
      vw - POP_WIDTH - margin
    )

    // Decide above vs below based on space available
    const spaceAbove = rect.top
    const spaceBelow = vh - rect.bottom
    const wantAbove = direction === 'above'
    const canAbove = spaceAbove >= POP_MAX_HEIGHT + margin
    const canBelow = spaceBelow >= POP_MAX_HEIGHT + margin
    const useAbove = wantAbove ? (canAbove || !canBelow) : !(canBelow || !canAbove)

    const top = useAbove
      ? Math.max(margin, rect.top - POP_MAX_HEIGHT - 6)
      : Math.min(vh - POP_MAX_HEIGHT - margin, rect.bottom + 6)

    setPopPos({ left, top, flipped: !useAbove })
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    reposition()
    const onDown = (e) => {
      if (popRef.current?.contains(e.target)) return
      if (btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onResize = () => reposition()
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [open])

  // Autofocus the search box when opening
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0)
  }, [open])

  const insert = (emoji) => {
    // Preferred path: textarea + controlled state — insert at caret
    if (textareaRef?.current && typeof value === 'string' && setValue) {
      const el = textareaRef.current
      const start = el.selectionStart ?? value.length
      const end = el.selectionEnd ?? value.length
      const next = value.slice(0, start) + emoji + value.slice(end)
      setValue(next)
      setTimeout(() => {
        el.focus()
        const pos = start + emoji.length
        el.setSelectionRange(pos, pos)
      }, 0)
    }
    onInsert?.(emoji)
    // Keep picker open so people can add several emojis in a row; closing
    // feels finicky when you're trying to add 🎉 + 🚀 at once.
  }

  const searchResults = query ? searchEmojis(query) : null
  const activeCategory = EMOJI_CATEGORIES.find((c) => c.id === activeCat)

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={title}
        aria-label={title}
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: open ? '#eff6ff' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 6,
          fontSize: 16,
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
          color: 'var(--muted)',
        }}
      >
        😀
      </button>

      {open && (
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            left: popPos.left,
            top: popPos.top,
            width: POP_WIDTH,
            maxHeight: POP_MAX_HEIGHT,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            zIndex: 1200,
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            boxSizing: 'border-box',
          }}
        >
          {/* Search */}
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search emoji…"
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />

          {/* Category tabs — hide while searching */}
          {!query && (
            <div
              style={{
                display: 'flex',
                gap: 4,
                overflowX: 'auto',
                paddingBottom: 2,
              }}
            >
              {EMOJI_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCat(c.id)}
                  style={{
                    flexShrink: 0,
                    padding: '3px 8px',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.06,
                    background: c.id === activeCat ? 'var(--accent-bg)' : '#fff',
                    color: c.id === activeCat ? 'var(--accent)' : 'var(--muted)',
                    cursor: 'pointer',
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {/* Emoji grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 2,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            {(searchResults ?? activeCategory?.emojis ?? []).map((emoji, i) => (
              <button
                key={`${emoji}-${i}`}
                type="button"
                onClick={() => insert(emoji)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 18,
                  cursor: 'pointer',
                  padding: 0,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {emoji}
              </button>
            ))}
            {searchResults && searchResults.length === 0 && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: '20px 8px',
                  textAlign: 'center',
                  fontSize: 11,
                  color: 'var(--muted-light)',
                  fontStyle: 'italic',
                }}
              >
                No matches for "{query}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
